/** Host OS — Team. Invite co-hosts & staff and assign roles (preview). */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UsersRound, UserPlus, Crown, Shield, ScanLine, Trash2, Mail, ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/App";
import HostLayout from "@/components/host/HostLayout";
import { HostPageHeader, PreviewBadge } from "@/components/host/hostUi";

const initials = (n) => (!n ? "?" : n.split(" ").map((x) => x[0]).join("").toUpperCase().slice(0, 2));

const ROLES = {
  owner: { label: "Owner", icon: Crown, desc: "Full access to everything", cls: "bg-primary/15 text-primary-ink" },
  manager: { label: "Manager", icon: Shield, desc: "Manage events, attendees & messaging", cls: "bg-blue-500/15 text-blue-600 dark:text-blue-300" },
  checkin: { label: "Check-in staff", icon: ScanLine, desc: "Check attendees in on event day only", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" },
};

const HostTeamPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("manager");
  const [members, setMembers] = useState(() => [
    { id: "owner", name: user?.name || "You", email: user?.email, picture: user?.picture, role: "owner", status: "active" },
  ]);

  const invite = () => {
    const e = email.trim().toLowerCase();
    if (!e || !/^\S+@\S+\.\S+$/.test(e)) { toast.error("Enter a valid email"); return; }
    if (members.some((m) => (m.email || "").toLowerCase() === e)) { toast.error("That person is already on your team"); return; }
    setMembers((p) => [...p, { id: e, name: e.split("@")[0], email: e, role, status: "invited" }]);
    setEmail("");
    toast.success(`Invite queued for ${e} (preview)`);
  };

  const remove = (id) => setMembers((p) => p.filter((m) => m.id !== id));

  return (
    <HostLayout>
      <div data-testid="host-team-page">
        <button onClick={() => navigate("/host/settings")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="team-back-settings">
          <ArrowLeft className="w-4 h-4" /> Settings
        </button>
        <HostPageHeader
          icon={UsersRound}
          title="Team"
          preview
          subtitle="Bring co-hosts and staff on board to help run your events, with roles that control what they can do."
        />

        {/* Invite */}
        <Card className="p-5 mb-5" data-testid="team-invite">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus className="w-4 h-4 text-primary-ink" />
            <h2 className="font-display text-lg">Invite a teammate</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@email.com" className="pl-9" data-testid="team-invite-email" onKeyDown={(e) => e.key === "Enter" && invite()} />
            </div>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="sm:w-52" data-testid="team-invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="checkin">Check-in staff</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={invite} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5" data-testid="team-invite-send">
              <UserPlus className="w-4 h-4" /> Invite
            </Button>
          </div>
        </Card>

        {/* Members */}
        <h2 className="font-display text-xl mb-3">Team members</h2>
        <Card className="divide-y divide-border" data-testid="team-list">
          {members.map((m) => {
            const r = ROLES[m.role] || ROLES.manager;
            const RoleIcon = r.icon;
            return (
              <div key={m.id} className="flex items-center gap-3 p-4" data-testid={`team-member-${m.id}`}>
                <Avatar className="w-10 h-10 shrink-0 ring-1 ring-border">
                  <AvatarImage src={m.picture} alt={m.name} />
                  <AvatarFallback className="bg-primary/20 text-primary-ink text-sm">{initials(m.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{m.name}{m.status === "invited" && <span className="text-xs text-muted-foreground font-normal ml-2">· invited</span>}</p>
                  <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                </div>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${r.cls}`} title={r.desc}>
                  <RoleIcon className="w-3.5 h-3.5" /> {r.label}
                </span>
                {m.role !== "owner" && (
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(m.id)} aria-label="Remove" data-testid={`team-remove-${m.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            );
          })}
        </Card>

        {/* Role legend */}
        <div className="grid sm:grid-cols-3 gap-3 mt-5" data-testid="team-role-legend">
          {Object.entries(ROLES).map(([k, r]) => {
            const RoleIcon = r.icon;
            return (
              <Card key={k} className="p-4">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${r.cls}`}><RoleIcon className="w-4 h-4" /></div>
                <p className="text-sm font-medium">{r.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{r.desc}</p>
              </Card>
            );
          })}
        </div>
      </div>
    </HostLayout>
  );
};

export default HostTeamPage;
